/**
 * Central Google Analytics 4 (GA4) Event Tracking Utility
 *
 * This utility provides a centralized way to track events across all pages
 * with proper error handling and consent checking.
 */

import {
  hasMarketingConsent,
  setEnhancedConversionUserData,
  type EnhancedConversionInput,
} from "./enhancedConversions";

// === TYPES ===
export interface GA4EventParams {
  event_category?: string;
  event_label?: string;
  page_location?: string;
  page_title?: string;
  [key: string]: unknown;
}

export interface GA4EventOptions {
  page?: string;
  category?: string;
  label?: string;
  customParams?: Record<string, unknown>;
}

type GtagArgs = [command: string, ...params: unknown[]];
type GtagFunction = (...args: GtagArgs) => void;

declare global {
  interface Window {
    gtag?: GtagFunction;
  }
}

// === UTILITY FUNCTIONS ===

/**
 * Safe gtag wrapper that no-ops when GA is not ready
 */
export const safeGtag = (...args: GtagArgs): void => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
};

/**
 * Check if GA4 is available and user has consented to analytics
 */
export const isGA4Available = (): boolean => {
  if (typeof window === "undefined") return false;

  const gtag = window.gtag;
  if (typeof gtag !== "function") return false;

  try {
    const savedConsent = localStorage.getItem("cookieConsent");
    if (savedConsent) {
      const parsedConsent = JSON.parse(savedConsent);
      return parsedConsent.analytics === true;
    }
  } catch (error) {
    console.error("[GA4] Error checking analytics consent:", error);
  }

  return false;
};

/**
 * Central GA4 Event Tracking Function
 */
export const trackGAEvent = (
  action: string,
  options: GA4EventOptions = {},
  retryCount: number = 0
): void => {
  if (!isGA4Available()) {
    if (retryCount < 3) {
      setTimeout(() => {
        trackGAEvent(action, options, retryCount + 1);
      }, 200);
    }
    return;
  }

  try {
    const {
      page = "unknown",
      category = "general",
      label = "default",
      customParams = {},
    } = options;

    const eventParams: GA4EventParams = {
      event_category: category,
      event_label: label,
      page_location: window.location.href,
      page_title: document.title,
      page_name: page,
      ...customParams,
    };

    safeGtag("event", action, eventParams);
  } catch (error) {
    console.error("[GA4] Error tracking event:", error);
  }
};

/**
 * Track page views (useful for SPA navigation)
 */
export const trackPageView = (pageName: string, pageTitle?: string): void => {
  if (!isGA4Available()) return;

  try {
    safeGtag("event", "page_view", {
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      page_name: pageName,
    });
  } catch (error) {
    console.error("[GA4] Error tracking page view:", error);
  }
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (
  formName: string,
  formType: string = "contact",
  success: boolean = true
): void => {
  trackGAEvent("form_submit", {
    category: "form_interaction",
    label: formName,
    customParams: { form_type: formType, form_success: success },
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (
  buttonName: string,
  buttonLocation: string = "unknown",
  buttonType: string = "button"
): void => {
  trackGAEvent("button_click", {
    category: "user_interaction",
    label: buttonName,
    customParams: { button_location: buttonLocation, button_type: buttonType },
  });
};

/**
 * Track link clicks
 */
export const trackLinkClick = (
  linkText: string,
  linkUrl: string,
  linkType: "internal" | "external" = "internal"
): void => {
  trackGAEvent("link_click", {
    category: "navigation",
    label: linkText,
    customParams: { link_url: linkUrl, link_type: linkType },
  });
};

/**
 * Track video interactions
 */
export const trackVideoInteraction = (
  videoName: string,
  action: "play" | "pause" | "complete" | "seek",
  currentTime?: number,
  duration?: number
): void => {
  trackGAEvent("video_interaction", {
    category: "media",
    label: videoName,
    customParams: {
      video_action: action,
      video_current_time: currentTime,
      video_duration: duration,
    },
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth: number): void => {
  trackGAEvent("scroll_depth", {
    category: "engagement",
    label: "scroll",
    customParams: { scroll_depth: depth },
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm: string, resultsCount?: number): void => {
  trackGAEvent("search", {
    category: "search",
    label: searchTerm,
    customParams: { search_term: searchTerm, search_results: resultsCount },
  });
};

/**
 * Track errors
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorLocation?: string
): void => {
  trackGAEvent("error", {
    category: "error",
    label: errorType,
    customParams: { error_message: errorMessage, error_location: errorLocation },
  });
};

// === COOKIE CONSENT SPECIFIC TRACKING ===

export const trackCookieConsentEvent = (
  action: string,
  consentSettings: { analytics: boolean; marketing: boolean },
  page: string = "unknown"
): void => {
  trackGAEvent(action, {
    page,
    category: "cookie_consent",
    label: page,
    customParams: {
      consent_analytics: consentSettings.analytics,
      consent_marketing: consentSettings.marketing,
    },
  });
};

export const trackCookieConsentButton = (
  buttonType: "accept_all" | "accept_selected" | "reject_all" | "details_toggle",
  page: string = "unknown"
): void => {
  trackGAEvent(`cookie_${buttonType}`, {
    page,
    category: "cookie_consent",
    label: page,
    customParams: { button_type: buttonType },
  });
};

export const trackCookieToggle = (
  category: "analytics" | "marketing",
  enabled: boolean,
  page: string = "unknown"
): void => {
  trackGAEvent("cookie_toggle", {
    page,
    category: "cookie_consent",
    label: page,
    customParams: {
      cookie_category: category,
      cookie_enabled: enabled,
      toggle_action: enabled ? "enable" : "disable",
    },
  });
};

/**
 * Fire a Google Ads conversion, optionally as an Enhanced Conversion.
 *
 * Pass `userData` whenever the action that triggered the conversion gave us an
 * email, phone or name (form submit, confirmed booking). It is normalised,
 * SHA-256 hashed in the browser and attached to the tag before the conversion
 * event — see `@utils/enhancedConversions`.
 *
 * Gated on **marketing** consent, not analytics: the `AW-` tag is only
 * configured by `loadGoogleTags` when marketing is granted, so on an
 * analytics-only consent this event had no configured destination and was
 * dropped by gtag anyway.
 *
 * Awaiting the returned promise is optional — the conversion is fired either
 * way. Await it only if you need to know it went out before navigating away.
 */
export const trackGoogleAdsConversion = async (
  conversionId: string,
  conversionLabel: string,
  page: string = "unknown",
  userData?: EnhancedConversionInput
): Promise<void> => {
  if (!hasMarketingConsent()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  try {
    if (userData) {
      await setEnhancedConversionUserData(userData);
    }

    safeGtag("event", "conversion", {
      send_to: `${conversionId}/${conversionLabel}`,
      page_name: page,
    });
  } catch (error) {
    console.error("[Google Ads] Error tracking conversion:", error);
  }
};

const ga4Tracking = {
  trackGAEvent,
  trackPageView,
  trackFormSubmit,
  trackButtonClick,
  trackLinkClick,
  trackVideoInteraction,
  trackScrollDepth,
  trackSearch,
  trackError,
  trackCookieConsentEvent,
  trackCookieConsentButton,
  trackCookieToggle,
  trackGoogleAdsConversion,
  isGA4Available,
};

export default ga4Tracking;
